import type { UseMutationResult } from '@tanstack/react-query';
import type {
  DrakeChecklist,
  DrakeMatchStatus,
  ContactInfo,
  DrakeExtractionAnalytics,
  PasswordProtectedPdfError,
} from '../../types';
import type {
  DrakeUploadBatch,
  BatchUploadResponse,
  DrakeExtractionJob,
  BulkDeleteResult,
} from '../../services/api';

export const matchStatusDisplayNames: Record<DrakeMatchStatus, string> = {
  pending_review: 'Pending Review',
  matched: 'Matched',
  multiple_matches: 'Multiple Matches',
  no_match: 'No Match',
};

export const matchStatusColors: Record<DrakeMatchStatus, string> = {
  pending_review: '#F59E0B',
  matched: '#10B981',
  multiple_matches: '#8B5CF6',
  no_match: '#EF4444',
};

export interface UploadTabProps {
  isBatchMode: boolean;
  setIsBatchMode: (value: boolean) => void;
  uploading: boolean;
  activeJob: DrakeExtractionJob | null;
  jobPolling: boolean;
  setActiveJob: (job: DrakeExtractionJob | null) => void;
  clearJob: () => void;
  setSelectedTab: (tab: string) => void;
  handleFileUpload: (files: FileList | null, overwrite?: boolean) => void;
  handleDrop: (e: React.DragEvent) => void;
  handleDragOver: (e: React.DragEvent) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  batchFileInputRef: React.RefObject<HTMLInputElement>;
  batchUploading: boolean;
  activeBatch: DrakeUploadBatch | null;
  batchResponse: BatchUploadResponse | null;
  setActiveBatch: (batch: DrakeUploadBatch | null) => void;
  setBatchResponse: (response: BatchUploadResponse | null) => void;
  handleBatchUpload: (files: FileList | null) => void;
  clearBatch: () => void;
  uploadResult: DrakeChecklist | null;
  setUploadResult: (result: DrakeChecklist | null) => void;
  uploadError: string | null;
  setSelectedChecklist: (checklist: DrakeChecklist | null) => void;
  setDetailDialogOpen: (open: boolean) => void;
  setMatchDialogOpen: (open: boolean) => void;
}

export interface ChecklistsTabProps {
  checklists: DrakeChecklist[] | undefined;
  checklistsLoading: boolean;
  selectedChecklistIds: Set<string>;
  setSelectedChecklistIds: (ids: Set<string>) => void;
  bulkDeleteConfirmOpen: boolean;
  setBulkDeleteConfirmOpen: (open: boolean) => void;
  bulkDeleteMutation: UseMutationResult<BulkDeleteResult, Error, string[], unknown>;
  setSelectedChecklist: (checklist: DrakeChecklist | null) => void;
  setDetailDialogOpen: (open: boolean) => void;
  setMatchDialogOpen: (open: boolean) => void;
  navigate: (path: string) => void;
}

export interface PendingReviewTabProps {
  pendingReview: DrakeChecklist[] | undefined;
  pendingLoading: boolean;
  setSelectedChecklist: (checklist: DrakeChecklist | null) => void;
  setDetailDialogOpen: (open: boolean) => void;
  setMatchDialogOpen: (open: boolean) => void;
}

export interface AnalyticsTabProps {
  analytics: DrakeExtractionAnalytics | undefined;
  analyticsLoading: boolean;
}

export interface MatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedChecklist: DrakeChecklist | null;
  matchCandidates: ContactInfo[] | undefined;
  candidatesLoading: boolean;
  matchMutation: UseMutationResult<DrakeChecklist, Error, { checklistId: string; clientId: string }, unknown>;
}

export interface ChecklistDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedChecklist: DrakeChecklist | null;
  setSelectedChecklist: (checklist: DrakeChecklist | null) => void;
  editingField: string | null;
  setEditingField: (field: string | null) => void;
  editValue: string;
  setEditValue: (value: string) => void;
  feedbackSubmitting: boolean;
  handleSubmitCorrection: (field: string, originalValue: string, correctedValue: string) => void;
  receivedItems: Set<number>;
  setReceivedItems: (items: Set<number>) => void;
  deleteConfirmIndex: number | null;
  setDeleteConfirmIndex: (index: number | null) => void;
  updateReceivedMutation: UseMutationResult<DrakeChecklist, Error, { checklistId: string; itemIndex: number; received: boolean }, unknown>;
  deleteItemMutation: UseMutationResult<DrakeChecklist, Error, { checklistId: string; itemIndex: number }, unknown>;
  deleteChecklistConfirmOpen: boolean;
  setDeleteChecklistConfirmOpen: (open: boolean) => void;
  deleteChecklistMutation: UseMutationResult<void, Error, string, unknown>;
  setMatchDialogOpen: (open: boolean) => void;
  navigate: (path: string) => void;
}

export interface DuplicateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingChecklist: DrakeChecklist | null;
  taxYear: number;
  pendingFile: File | null;
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleFileUpload: (files: FileList | null, overwrite?: boolean) => void;
  setExistingChecklistForDuplicate: (checklist: DrakeChecklist | null) => void;
  setPendingFile: (file: File | null) => void;
}

export interface PasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  passwordPdfInfo: PasswordProtectedPdfError | null;
  pdfPassword: string;
  setPdfPassword: (password: string) => void;
  passwordRetrying: boolean;
  passwordError: string | null;
  handlePasswordRetry: () => void;
  setPasswordPdfInfo: (info: PasswordProtectedPdfError | null) => void;
  setPasswordError: (error: string | null) => void;
}
